import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { apiClient, ApiError } from '@/lib/api';
/**
 * PUT /sslwidewatch/auth/reset-password?token=...&email=...&password=...
 * The backend returns a plain string: "" / "Password updated" on success,
 * or an error message on failure. We rely on HTTP status + basic string
 * sniff to decide.
 */
export function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') ?? '';
    const email = params.get('email') ?? '';
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    async function onSubmit(e) {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setPending(true);
        try {
            const res = await apiClient.put('/sslwidewatch/auth/reset-password', undefined, {
                anonymous: true,
                query: { token, email, password },
                accept: 'text/plain',
            });
            if (typeof res === 'string' && res.toLowerCase().startsWith('invalid')) {
                setError(res);
                setPending(false);
                return;
            }
            navigate('/login?reset=1', { replace: true });
        }
        catch (err) {
            const message = err instanceof ApiError ? err.message : 'Reset failed.';
            setError(message);
            setPending(false);
        }
    }
    const linkInvalid = !token || !email;
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "text-center space-y-2", children: [_jsx("div", { className: "mx-auto grid h-12 w-12 place-items-center rounded-[var(--r-md)] border border-[var(--mint-200)] text-[var(--emerald-text)]", style: {
                            background: 'linear-gradient(135deg, var(--mint-100), var(--mint-50))',
                        }, children: _jsx(ShieldCheckIcon, { className: "h-5 w-5" }) }), _jsxs("h1", { className: "m-0 font-normal tracking-[-0.02em] text-[var(--ink-900)] [font-size:clamp(24px,3vw,30px)] leading-tight", style: { fontFamily: 'var(--f-display)' }, children: ["Set your new ", _jsx("em", { className: "italic", children: "password" })] }), _jsx("p", { className: "text-[14px] text-[var(--ink-500)]", children: "Choose something strong \u2014 at least 8 characters." })] }), linkInvalid ? (_jsx("div", { role: "alert", className: "rounded-[var(--r-md)] border border-[var(--rose-200)] bg-[var(--rose-50)] px-3 py-2 text-[13px] text-[var(--crimson)]", children: "This password-reset link is incomplete. Please request a new one." })) : (_jsxs("form", { onSubmit: onSubmit, className: "space-y-4", noValidate: true, children: [_jsx(Field, { id: "rp-new", label: "New password", type: "password", value: password, onChange: setPassword, autoComplete: "new-password" }), _jsx(Field, { id: "rp-confirm", label: "Confirm new password", type: "password", value: confirm, onChange: setConfirm, autoComplete: "new-password" }), error && (_jsx("div", { role: "alert", className: "rounded-[var(--r-md)] border border-[var(--rose-200)] bg-[var(--rose-50)] px-3 py-2 text-[13px] text-[var(--crimson)]", children: error })), _jsx(Button, { variant: "primary", type: "submit", disabled: pending, className: "w-full", children: pending ? 'Updating…' : 'Set password' })] })), _jsx("p", { className: "text-center text-[13px] text-[var(--ink-500)]", children: _jsx(Link, { to: "/login", className: "font-medium text-[var(--royal)] no-underline hover:underline", children: "Back to sign in" }) })] }));
}
function Field({ id, label, type, value, onChange, autoComplete, }) {
    return (_jsxs("label", { htmlFor: id, className: "block space-y-1.5", children: [_jsx("span", { className: "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: label }), _jsx("input", { id: id, type: type, value: value, onChange: (e) => onChange(e.target.value), autoComplete: autoComplete, required: true, className: "w-full rounded-[var(--r-md)] border border-[var(--line)] bg-white px-3.5 py-2.5 text-[14px] text-[var(--ink-900)] focus:border-[var(--lav-200)] focus:outline-none focus:ring-2 focus:ring-[var(--lav-100)]" })] }));
}
